import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Avatar } from "@/components/Common/Avatar";
import { LoginHeader } from "@/components/Common/LoginHeader";
import { FacilityMapsLink } from "@/components/Facility/FacilityMapLink";

import useAppHistory from "@/hooks/useAppHistory";
import useFilters from "@/hooks/useFilters";

import query from "@/Utils/request/query";
import publicFacilityApi from "@/types/facility/publicFacilityApi";

import CareIcon from "@/CAREUI/icons/CareIcon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { FeatureBadge } from "./Utils";
import { UserCard } from "./components/UserCard";

interface Props {
  id: string;
}

export function FacilityDetailsPage({ id }: Props) {
  const { t } = useTranslation();
  const { goBack } = useAppHistory();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [visibleFeatures, setVisibleFeatures] = useState<number[]>([]);
  const [showViewMore, setShowViewMore] = useState(false);

  const { data: facilityResponse, isLoading } = useQuery({
    queryKey: ["facility", id],
    queryFn: query(publicFacilityApi.getAny, {
      pathParams: { id },
    }),
  });

  const { Pagination } = useFilters({
    limit: 18,
  });

  const { data: docResponse, error: docError } = useQuery({
    queryKey: [publicFacilityApi.listSchedulableUsers, id],
    queryFn: query(publicFacilityApi.listSchedulableUsers, {
      pathParams: { facilityId: id },
      silent: true,
    }),
  });

  if (docError) {
    toast.error(t("error_fetching_users_data"));
  }

  const users = docResponse?.results ?? [];

  const facility = facilityResponse;

  const calculateVisibleFeatures = useCallback(() => {
    const element = elementRef.current;
    if (!element || !facility?.features) return;

    const children = Array.from(element.children) as HTMLElement[];
    const containerWidth = element.offsetWidth;
    let totalWidth = 0;

    const childWidths = children.map((child) => {
      const width = child.offsetWidth + 8;
      totalWidth += width;
      return width;
    });

    if (totalWidth <= containerWidth) {
      setVisibleFeatures(facility.features);
      setShowViewMore(false);
      return;
    }

    const viewMoreButtonWidth = 90;
    let usedWidth = 0;
    const visible: number[] = [];

    childWidths.forEach((width, index) => {
      if (usedWidth + width + viewMoreButtonWidth <= containerWidth) {
        usedWidth += width;
        visible.push(facility.features[index]);
      }
    });

    setVisibleFeatures(visible);
    setShowViewMore(true);
  }, [facility?.features]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let frameId: number;

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        calculateVisibleFeatures();
      });
    });

    observer.observe(element);
    calculateVisibleFeatures();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [calculateVisibleFeatures]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin">
          <CareIcon icon="l-spinner" className="size-8" />
        </div>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">
            {t("facility_not_found")}
          </h2>
          <Button
            variant="outline"
            className="border border-secondary-400"
            onClick={() => goBack("/facilities")}
          >
            {t("back_to_facilities")}
          </Button>
        </Card>
      </div>
    );
  }

  const hiddenFeatures =
    facility.features?.filter(
      (featureId: number) => !visibleFeatures.includes(featureId),
    ) ?? [];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center pb-4">
        <Button
          variant="outline"
          className="border border-secondary-400"
          onClick={() => goBack("/facilities")}
        >
          <ArrowLeftIcon className="size-4 mr-1" />
          <span className="text-sm underline">{t("back")}</span>
        </Button>
        <LoginHeader />
      </div>
      <div className="bg-blue-50 rounded-lg">
        <div className="flex flex-col px-6 xl:px-20 py-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-x-10 gap-y-2 pb-3">
            <div className="size-16 shrink-0 overflow-hidden rounded-lg">
              <Avatar
                imageUrl={facility.read_cover_image_url}
                name={facility.name || ""}
              />
            </div>

            <div className="flex flex-col gap-y-2">
              <h1 className="text-xl sm:text-3xl font-bold break-words text-center sm:text-start">
                {facility.name}
              </h1>
              <p className="text-sm text-gray-500 text-center sm:text-start">
                {[facility.address].filter(Boolean).join(", ")}
                <span>
                  , {facility.geo_organization.parent?.name},{" "}
                  {facility.geo_organization.parent?.parent?.name},{" "}
                  {facility.pincode}
                </span>
                {facility.latitude && facility.longitude && (
                  <span className="ml-2 sm:ml-16 inline-block">
                    <FacilityMapsLink
                      latitude={facility.latitude}
                      longitude={facility.longitude}
                    />
                  </span>
                )}
              </p>
            </div>
          </div>
          <hr className="hidden sm:block border-t border-gray-200" />
          <div className="hidden sm:flex gap-x-4 pt-3">
            <h5 className="text-sm font-semibold shrink-0 pt-0.5">
              {t("our_services")}
            </h5>
            <div className="relative w-full">
              <div
                ref={elementRef}
                className="absolute invisible pointer-events-none w-full flex flex-wrap items-start gap-2"
              >
                {facility.features?.map((featureId) => (
                  <FeatureBadge
                    key={featureId}
                    featureId={featureId as number}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-2 max-h-[2rem] overflow-hidden">
                {visibleFeatures.map((featureId: number) => (
                  <FeatureBadge key={featureId} featureId={featureId} />
                ))}
                {showViewMore && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="link" size="xs" className="px-0">
                        {t("view_more")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" className="w-[30rem]">
                      <div className="w-full bg-gray-50 border border-gray-50 rounded-md p-3">
                        <div className="flex flex-wrap items-start gap-2">
                          {hiddenFeatures.map((featureId) => (
                            <FeatureBadge
                              key={featureId}
                              featureId={featureId as number}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>
          <Accordion
            type="single"
            collapsible
            className="w-full sm:hidden pt-4"
          >
            <AccordionItem
              className="flex flex-col gap-y-3 bg-gray-50 shadow rounded-md px-3"
              value={"our-services"}
            >
              <AccordionTrigger className="flex justify-between">
                <h5 className="text-sm font-semibold">{t("our_services")}</h5>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap items-start gap-2">
                  {facility.features?.map((featureId) => (
                    <FeatureBadge
                      key={featureId}
                      featureId={featureId as number}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <div className="mt-6">
        {users.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {users?.map((user) => (
                <UserCard key={user.username} user={user} facilityId={id} />
              ))}
            </div>
            <Pagination totalCount={docResponse?.count ?? 0} />
          </>
        )}
        {users.length === 0 && (
          <div className="h-full space-y-2 rounded-lg bg-white p-7 shadow-sm">
            <div className="flex w-full items-center justify-center text-xl font-bold text-secondary-500">
              {t("no_users_found")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
